/**
 * Chat (lobby and match): plain text between people, relayed by the room — never a sim intent,
 * so it stays out of the deterministic state and the replay log.
 *
 *   client → server  `chat { channel, to?, text, loc? }`
 *   server → client  `chat { channel, from, name, to?, toName?, text, loc? }` to the recipients only
 *                    (the sender gets its own line back: the echo says it went out) ·
 *                    `chat.error { code, msg }` to the sender
 *
 * Channels: `all` (everyone in the room / match), `ally` (our ally or teammates — resolved by the
 * server from the sim's alliances / teams when sent) and `pm` (one human player, `to` = their
 * player id; bots cannot read). Eliminated players may use every channel — by design: a fallen
 * nation may tell its friends what it saw. `loc` is a map point (mu) the recipients can fly to.
 * Text is cleaned by `cleanChatText` (protocol) and must be drawn as text, never as HTML.
 */
import type { PlayerId } from '@sa/sim';

export type ChatChannel = 'all' | 'ally' | 'pm';
export const CHAT_CHANNELS: readonly ChatChannel[] = ['all', 'ally', 'pm'];
/** characters per message, after cleaning */
export const CHAT_MAX = 160;
/** per user: at most CHAT_BURST messages, refilled at CHAT_BURST per CHAT_WINDOW_MS */
export const CHAT_BURST = 5;
export const CHAT_WINDOW_MS = 10_000;

export interface ChatLoc {
  x: number;
  y: number;
}

export interface ChatSend {
  type: 'chat';
  channel: ChatChannel;
  /** pm: the player id to tell */
  to?: PlayerId;
  text: string;
  loc?: ChatLoc;
}

export interface ChatMessage {
  type: 'chat';
  channel: ChatChannel;
  /** the sender's player id (0 in the lobby) */
  from: PlayerId;
  name: string;
  /** pm: who it was for (so the sender's echo reads `→ KESTREL`) */
  to?: PlayerId;
  toName?: string;
  text: string;
  loc?: ChatLoc;
}

export type ChatErrorCode = 'slowDown' | 'noAlly' | 'noTarget' | 'notInRoom' | 'allOnly';

export interface ChatErrorMessage {
  type: 'chat.error';
  code: ChatErrorCode;
  msg: string;
}

// control characters (C0, DEL, C1), zero-width / bidi overrides, line and paragraph separators
// eslint-disable-next-line no-control-regex
const STRIP = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2066-\u2069\ufeff]/g;

/**
 * A chat line as it may travel: control and invisible / direction-changing characters dropped,
 * runs of spaces collapsed, trimmed. Null when nothing is left or it is longer than CHAT_MAX.
 */
export function cleanChatText(raw: string): string | null {
  const t = raw.replace(STRIP, ' ').replace(/\s+/g, ' ').trim();
  return t.length > 0 && t.length <= CHAT_MAX ? t : null;
}
